import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../App';
import { pushEventToParent } from '../../../Utils/eventUtils';
import { ROUTES } from '../../../Utils/routes';
import { captureException } from '../../../Utils/sentryUtils';
import {
  composeNudgeStackData,
  hasSeenDefaultNudges,
  markDefaultNudgesSeen,
  rotateNudgesToId,
} from './defaultNudges';
import JFSNudgeStack from './JFSNudgeStack';
import styles from './JFSNudgeStackPage.module.css';
import {
  clearJFSInitialNudgeId,
  getJFSNudgesData,
} from './navigationHelper';
import { Nudge } from './types';

const JFSNudgeStackPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    setCurrentRoute,
    setBackButtonHandler,
    nudges: dynamicNudges,
  } = useContext<any>(AppContext);

  const nudgesData = getJFSNudgesData();
  const { returnRoute, nudgeStackConfig } = nudgesData || {};
  const [initialNudgeId] = useState<string | undefined>(
    nudgesData?.initialNudgeId,
  );

  // Read once on mount so the order stays stable for this visit
  const [defaultsFirst] = useState(() => !hasSeenDefaultNudges());

  const nudges = useMemo(
    () =>
      rotateNudgesToId(
        composeNudgeStackData(dynamicNudges, defaultsFirst),
        initialNudgeId,
      ),
    [dynamicNudges, defaultsFirst, initialNudgeId]
  );

  useEffect(() => {
    markDefaultNudgesSeen();
    clearJFSInitialNudgeId();
  }, []);

  const handleClose = () => {
    try {
      const targetRoute = returnRoute || ROUTES.DASHBOARD;
      if (setCurrentRoute) {
        setCurrentRoute(targetRoute);
      } else {
        navigate(targetRoute);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      captureException(error);
      navigate(ROUTES.DASHBOARD);
    }
  };

  const reportedNudgeIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    nudges.forEach((nudge: Nudge) => {
      if (reportedNudgeIds.current.has(nudge.nudgeId)) return;
      reportedNudgeIds.current.add(nudge.nudgeId);
      try {
        pushEventToParent({
          eventCode: 'NUDGE_RENDERED_' + nudge.category,
        });
      } catch (error) {
        captureException(error);
      }
    });
  }, [nudges]);

  useEffect(() => {
    setBackButtonHandler(() => {
      return () => {
        handleClose();
      };
    });
  }, []);

  const handleSwipeLeft = (nudge: Nudge) => {
    // console.log('Swiped left :', nudge.nudgeId);
  };

  const handleSwipeRight = (nudge: Nudge) => {
    // console.log('Swiped right :', nudge.nudgeId);
  };

  if (!nudges || nudges.length === 0) {
    return (
      <div className={styles.noNudgesContainer}>
        <div className={styles.noNudgesContent}>
          <h2 className={styles.noNudgesHeading}>
            You have no insights today, please check back tomorrow.
          </h2>
          <button onClick={handleClose} className={styles.goBackButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <JFSNudgeStack
      data={nudges}
      onClose={handleClose}
      onSwipeLeft={handleSwipeLeft}
      onSwipeRight={handleSwipeRight}
      {...nudgeStackConfig}
    />
  );
};

export default JFSNudgeStackPage;
